import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, CheckCircle2, Lock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { BLUR_DATA_URL } from '@/lib/utils';

type ConfierBienProfileCardProps = {
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  badge: string;
  benefits: readonly string[];
  image: string;
  imageAlt: string;
  gradient: string;
  actionLabel: string;
  href?: string;
  onClick?: () => void;
  requiresAuth?: boolean;
  authBadgeLabel?: string;
};

function CardContent({
  title,
  subtitle,
  description,
  icon: Icon,
  badge,
  benefits,
  image,
  imageAlt,
  gradient,
  actionLabel,
  requiresAuth = false,
  authBadgeLabel = 'Connexion requise',
}: Omit<ConfierBienProfileCardProps, 'href' | 'onClick'>) {
  return (
    <>
      <div className="relative h-56 sm:h-60 overflow-hidden shrink-0">
        <Image
          src={image}
          alt={imageAlt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
          placeholder="blur"
          blurDataURL={BLUR_DATA_URL}
          className="object-cover transition-transform duration-700 ease-premium group-hover:scale-105"
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${gradient} opacity-60`} />

        <span className="absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground shadow-sm">
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {badge}
        </span>

        {requiresAuth && (
          <span className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-surface/95 px-3 py-1 text-xs font-semibold text-primary shadow-sm">
            <Lock className="h-3.5 w-3.5" aria-hidden />
            {authBadgeLabel}
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-7 md:p-8">
        <div className="flex items-start gap-4 mb-4">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/[0.08] text-primary border border-border-subtle">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h2 className="font-display text-xl text-ink leading-tight">{title}</h2>
            <p className="text-xs font-medium mt-0.5 text-accent">{subtitle}</p>
          </div>
        </div>

        <p className="text-sm text-ink-secondary leading-relaxed mb-6">{description}</p>

        <ul className="space-y-2 mb-8">
          {benefits.map((benefit) => (
            <li key={benefit} className="flex items-center gap-2.5 text-sm text-ink-secondary">
              <CheckCircle2 className="h-4 w-4 text-accent shrink-0" aria-hidden />
              {benefit}
            </li>
          ))}
        </ul>

        <p className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-accent transition-colors">
          {actionLabel}
          <ArrowRight className="h-4 w-4 transition-transform duration-250 group-hover:translate-x-0.5" aria-hidden />
        </p>
      </div>
    </>
  );
}

export function ConfierBienProfileCard({
  href,
  onClick,
  title,
  subtitle,
  description,
  icon,
  badge,
  benefits,
  image,
  imageAlt,
  gradient,
  actionLabel,
  requiresAuth,
  authBadgeLabel,
}: ConfierBienProfileCardProps) {
  const sharedClassName =
    'group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border-subtle bg-surface text-left shadow-card transition-all duration-500 ease-premium hover:-translate-y-1 hover:border-accent/30 hover:shadow-card-hover focus-visible:outline-none focus-visible:shadow-focus';

  const ariaLabel = `${title} - ${actionLabel}`;

  if (href) {
    return (
      <Link href={href} className={sharedClassName} aria-label={ariaLabel}>
        <CardContent
          title={title}
          subtitle={subtitle}
          description={description}
          icon={icon}
          badge={badge}
          benefits={benefits}
          image={image}
          imageAlt={imageAlt}
          gradient={gradient}
          actionLabel={actionLabel}
          requiresAuth={requiresAuth}
          authBadgeLabel={authBadgeLabel}
        />
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={`${sharedClassName} w-full`} aria-label={ariaLabel}>
      <CardContent
        title={title}
        subtitle={subtitle}
        description={description}
        icon={icon}
        badge={badge}
        benefits={benefits}
        image={image}
        imageAlt={imageAlt}
        gradient={gradient}
        actionLabel={actionLabel}
        requiresAuth={requiresAuth}
        authBadgeLabel={authBadgeLabel}
      />
    </button>
  );
}
